/**
 * Bond Mentor — Fear Gauge Data (Drop 4)
 *
 * Credit spreads (IG + HY), spread divergence detection,
 * MOVE vs. VIX volatility comparison, and regime identification.
 */

// ============================================
// SPREAD ZONES (historical context bands)
// ============================================
export const SPREAD_ZONES = {
  CALM: { label: 'Calm', color: '#22c55e', igRange: 'Below 120 bps', hyRange: 'Below 380 bps' },
  ELEVATED: { label: 'Elevated', color: '#eab308', igRange: '120-180 bps', hyRange: '380-550 bps' },
  STRESSED: { label: 'Stressed', color: '#f97316', igRange: '180-250 bps', hyRange: '550-750 bps' },
  CRISIS: { label: 'Crisis', color: '#ef4444', igRange: 'Above 250 bps', hyRange: 'Above 750 bps' },
};

// ============================================
// VOLATILITY REGIMES
// ============================================
export const VOL_REGIMES = {
  LOW: { label: 'Low Volatility', color: '#22c55e', meaning: 'Calm conditions. Markets are complacent. Often a good time to be invested, but extreme low-vol periods sometimes precede sudden spikes.' },
  NORMAL: { label: 'Normal', color: '#3b82f6', meaning: 'Typical market fluctuations. Standard risk-management practices are sufficient.' },
  ELEVATED: { label: 'Elevated', color: '#eab308', meaning: 'Above-average turbulence. Markets are uncertain. Wider daily swings. Tighter risk management warranted.' },
  CRISIS: { label: 'Crisis', color: '#ef4444', meaning: 'Extreme volatility. Markets in panic mode. Large daily swings. Historically, these episodes create major buying opportunities for long-term investors — but only AFTER the volatility subsides.' },
};

// ============================================
// FEAR GAUGE DATA PER SCENARIO
// ============================================
export const FEAR_GAUGE_SCENARIOS = {
  ALL_CLEAR: {
    // Investment Grade Credit Spreads
    ig: {
      current: 95,
      weekAgo: 98,
      monthAgo: 102,
      yearAvg: 110,
      zone: 'CALM',
      trend: 'TIGHTENING',
      trendColor: '#22c55e',
      trendLabel: 'Tightening — Improving',
      note: 'Investment-grade credit spreads are well within the "calm" zone and continuing to tighten. Corporate borrowers are in healthy shape. Investors are willing to lend cheaply to high-quality companies.',
    },

    // High Yield (Junk Bond) Credit Spreads
    hy: {
      current: 320,
      weekAgo: 328,
      monthAgo: 345,
      yearAvg: 370,
      zone: 'CALM',
      trend: 'TIGHTENING',
      trendColor: '#22c55e',
      trendLabel: 'Tightening — Improving',
      note: 'High-yield spreads are tight and tightening further. Even riskier corporate borrowers are finding easy access to credit. This is a sign of strong risk appetite in the market.',
    },

    // Spread Divergence
    divergence: {
      detected: false,
      level: 'NONE',
      color: '#22c55e',
      note: 'No divergence detected. Both IG and HY spreads are moving in the same direction (tightening). This is consistent and healthy behavior.',
    },

    // MOVE Index (Bond Volatility)
    move: {
      current: 88,
      zone: 'LOW',
      zoneColor: '#22c55e',
      note: 'Bond market volatility is low. Treasury markets are trading in orderly fashion with no signs of stress.',
    },

    // VIX (Stock Volatility)
    vix: {
      current: 14.5,
      zone: 'LOW',
      zoneColor: '#22c55e',
      note: 'Stock market volatility is low. Investors are calm and options pricing reflects minimal fear.',
    },

    // MOVE vs VIX Disconnect
    disconnect: {
      detected: false,
      level: 8,
      color: '#22c55e',
      label: 'No Disconnect',
      note: 'Bond volatility and stock volatility are both low and in agreement. No hidden danger signals.',
    },

    // Volatility Regime
    regime: 'LOW',

    // Overall Analysis
    analysis: 'The credit market is healthy across the board. Investment-grade spreads are tight at 95 basis points, high-yield at 320 basis points — both well within calm historical ranges. No divergence between IG and HY behavior. The MOVE Index at 88 and VIX at 14.5 are both in low-volatility territory with no disconnect. There is no fear signal from credit or volatility markets right now.',
  },

  EARLY_WARNING: {
    ig: {
      current: 130,
      weekAgo: 125,
      monthAgo: 108,
      yearAvg: 110,
      zone: 'ELEVATED',
      trend: 'WIDENING',
      trendColor: '#eab308',
      trendLabel: 'Widening — Deteriorating',
      note: 'IG spreads have pushed above the "calm" zone into "elevated" territory. The trend has been widening consistently for the past month. Investors are beginning to demand more compensation for lending to corporations.',
    },
    hy: {
      current: 420,
      weekAgo: 405,
      monthAgo: 365,
      yearAvg: 370,
      zone: 'ELEVATED',
      trend: 'WIDENING',
      trendColor: '#f97316',
      trendLabel: 'Widening — Warning',
      note: 'High-yield spreads are widening faster than IG — this is an important early signal. Junk bonds are the "canary in the coal mine." When HY spreads widen faster than IG, it means stress is building from the bottom of the corporate credit stack upward.',
    },
    divergence: {
      detected: true,
      level: 'EARLY',
      color: '#eab308',
      note: 'EARLY DIVERGENCE DETECTED: HY spreads are widening faster than IG spreads. HY has widened 55 bps this month vs. only 22 bps for IG. Historically, when junk bonds start deteriorating while investment-grade remains relatively calm, broader credit stress follows within weeks to months.',
    },
    move: {
      current: 115,
      zone: 'ELEVATED',
      zoneColor: '#eab308',
      note: 'Bond volatility is elevated. Treasury markets are experiencing wider-than-normal daily swings. Smart money is actively repositioning.',
    },
    vix: {
      current: 18.2,
      zone: 'NORMAL',
      zoneColor: '#3b82f6',
      note: 'Stock volatility is still in the normal range. Stocks have not fully priced in the stress that bonds are showing.',
    },
    disconnect: {
      detected: true,
      level: 42,
      color: '#eab308',
      label: 'Moderate Disconnect',
      note: 'WARNING: Bond volatility (MOVE 115) is elevated while stock volatility (VIX 18.2) is still relatively calm. This disconnect means the bond market sees danger that the stock market has not yet acknowledged. Historically, stocks catch up (down) within weeks when this gap appears.',
    },
    regime: 'ELEVATED',
    analysis: 'Credit conditions are deteriorating. IG spreads have widened to 130 bps (elevated zone), and HY spreads to 420 bps — widening faster than IG, which is a classic early-warning divergence. The MOVE/VIX disconnect at 42/100 tells you that bond traders see more risk than stock traders. When bonds and stocks disagree like this, bonds are almost always right. The fear gauge is flashing yellow — take notice.',
  },

  DANGER_ZONE: {
    ig: {
      current: 210,
      weekAgo: 195,
      monthAgo: 155,
      yearAvg: 110,
      zone: 'STRESSED',
      trend: 'BLOWING_OUT',
      trendColor: '#ef4444',
      trendLabel: 'Blowing Out — Crisis Approaching',
      note: 'Investment-grade spreads have surged into the "stressed" zone, nearly doubling their year average. Even the highest-quality corporate borrowers are seeing their borrowing costs spike. This signals broad-based credit market stress.',
    },
    hy: {
      current: 650,
      weekAgo: 610,
      monthAgo: 480,
      yearAvg: 370,
      zone: 'STRESSED',
      trend: 'BLOWING_OUT',
      trendColor: '#ef4444',
      trendLabel: 'Blowing Out — Near Crisis',
      note: 'High-yield spreads have exploded to 650 bps — approaching crisis levels. Companies with weaker credit ratings are being effectively locked out of the bond market. Default risk is rising. Some companies will not survive this environment.',
    },
    divergence: {
      detected: true,
      level: 'SEVERE',
      color: '#ef4444',
      note: 'SEVERE DIVERGENCE: HY spreads have blown out to 650 bps while IG is at 210 bps. The ratio between them has widened dramatically, meaning stress is concentrated at the weakest companies. This pattern preceded major default waves in 2008 and 2020.',
    },
    move: {
      current: 165,
      zone: 'CRISIS',
      zoneColor: '#ef4444',
      note: 'Bond volatility is at crisis levels. Treasury markets are dislocated. Large, erratic daily swings. Market-makers are pulling back, reducing liquidity.',
    },
    vix: {
      current: 32.5,
      zone: 'ELEVATED',
      zoneColor: '#eab308',
      note: 'Stock volatility is elevated but STILL not at the crisis level that bond volatility has reached. Stocks are lagging behind the severity of the bond market stress.',
    },
    disconnect: {
      detected: true,
      level: 78,
      color: '#ef4444',
      label: 'Severe Disconnect',
      note: 'CRITICAL: Bond volatility (MOVE 165, crisis) is far ahead of stock volatility (VIX 32.5, elevated). This extreme disconnect means stocks have NOT finished falling. In every past instance where MOVE was this far ahead of VIX, stocks experienced additional significant declines before the gap closed. Do NOT bottom-fish.',
    },
    regime: 'CRISIS',
    analysis: 'Credit markets are in severe distress. IG spreads at 210 bps and HY at 650 bps are both in the "stressed" zone with HY approaching crisis levels. The spread divergence is severe — weak companies are being crushed. The MOVE/VIX disconnect at 78/100 is among the highest readings in the past decade, meaning stocks have MORE downside ahead. Bond volatility is at crisis levels that historically coincide with major financial market disruptions. This is a defensive-positioning emergency.',
  },
};
